import { initHttpAgent } from "@/http"
import { CenteredLoadingWavelengthRadio } from "@/icons/LoadingWavelengthRadio"
import { type SeoData, seoService } from "@/services/seoService"
import store from "@/store/store"
import ky from "ky"
import { useEffect, useState } from "react"
import Wavelength from "./Wavelength"

const createBootstrapAgent = () =>
  ky.create({
    credentials: "same-origin",
    prefixUrl: "/api",
    headers: { accept: "application/json; charset=utf8" },
    redirect: "follow",
    timeout: 15000, // 15 seconds
  })

const getMsgUUID = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const msgUUID = urlParams.get("msgUUID")

  return msgUUID
}

// TODO: use monorepo types
type InitData = {
  redirect?: boolean
  csrfToken: string
  seoData?: SeoData
}

const fetchInitData = async (msgUUID: string | null) => {
  const bootstrapAgent = createBootstrapAgent()

  // Include message UUID in bootstrap request if present
  const bootstrapUrl = msgUUID ? `bootstrap?msgUUID=${msgUUID}` : "bootstrap"
  const data = await bootstrapAgent.get<InitData>(bootstrapUrl).json()

  return data
}

const BootstrapContainer = () => {
  const [bootstrapData, setBootstrapData] = useState<InitData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchBootstrapData = async () => {
      try {
        setIsLoading(true)

        // Check for message UUID in URL query parameters
        const msgUUID = getMsgUUID()

        const data = await fetchInitData(msgUUID)

        if (data.redirect) {
          // Handle redirect case - go to 404 page
          window.location.href = "/404"
          return
        }

        setBootstrapData(data)

        // Initialize HTTP agent with CSRF token for subsequent requests
        initHttpAgent(data.csrfToken, store)

        // Apply SEO and tracking updates
        if (data.seoData) {
          seoService.updateSeoTags(data.seoData)
          seoService.injectTrackingScripts(data.seoData)
        }
      } catch (err_) {
        const err = err_ as Error

        console.error("Failed to fetch bootstrap data:", err)
        setError(err.message || "Failed to load application")

        // Show error state, but also provide fallback to refresh
        setTimeout(() => {
          if (window.confirm("Unable to load application. Would you like to refresh the page?")) {
            window.location.reload()
          }
        }, 3000)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchBootstrapData()
  }, [])

  if (isLoading) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CenteredLoadingWavelengthRadio />
        <div
          style={{
            marginTop: "20px",
            fontFamily: "sans-serif",
            color: "#666",
            fontSize: "16px",
          }}
        >
          Loading Wavelength...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "sans-serif",
            color: "#d32f2f",
            fontSize: "18px",
            marginBottom: "20px",
          }}
        >
          Unable to load application
        </div>
        <div
          style={{
            fontFamily: "sans-serif",
            color: "#666",
            fontSize: "14px",
            marginBottom: "20px",
          }}
        >
          {error}
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 20px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Refresh Page
        </button>
      </div>
    )
  }

  if (!bootstrapData) {
    return null // Shouldn't happen, but just in case
  }

  return <Wavelength store={store} bootstrapData={bootstrapData} />
}

export default BootstrapContainer
